import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

type Props = {
  ref: RefObject<HTMLDivElement | null>;
};

const findScrollableElement = (
  target: EventTarget | null,
  root: HTMLElement,
): HTMLElement | null => {
  let element = target instanceof HTMLElement ? target : null;

  while (element && root.contains(element)) {
    const { overflowX } = getComputedStyle(element);

    if (
      (overflowX === 'auto' || overflowX === 'scroll') &&
      element.scrollWidth > element.clientWidth
    ) {
      return element;
    }

    element = element.parentElement;
  }

  return null;
};

const useGrabScroll = ({ ref }: Props) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  const grabScroll = () => {
    const root = ref.current;

    if (!root) return;

    cleanupRef.current?.();

    let element: HTMLElement | null = null;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const onMouseDown = (e: MouseEvent) => {
      element = findScrollableElement(e.target, root);

      if (!element) return;

      isDown = true;
      startX = e.pageX;
      scrollLeft = element.scrollLeft;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown || !element) return;

      const dx = e.pageX - startX;
      element.scrollLeft = scrollLeft - dx;
    };

    const onMouseUp = () => {
      isDown = false;
    };

    root.addEventListener('mousedown', onMouseDown);
    globalThis.addEventListener('mousemove', onMouseMove);
    globalThis.addEventListener('mouseup', onMouseUp);

    cleanupRef.current = () => {
      root.removeEventListener('mousedown', onMouseDown);
      globalThis.removeEventListener('mousemove', onMouseMove);
      globalThis.removeEventListener('mouseup', onMouseUp);
      cleanupRef.current = null;
    };
  };

  useEffect(() => () => cleanupRef.current?.(), []);

  return { grabScroll };
};

export { useGrabScroll };
